import React from 'react';
import { v4 as UUID } from 'uuid';
import {
  CustomInput,
  CustomSelect,
  CustomToggle,
  CustomTextarea,
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';

function ConnectionObjectSchemaAttributes({
  schemaAttributes,
  setSchemaAttributes,
  canEdit,
  connectionObjectType,
}) {
  const attributeTypeOptions = [
    { value: 'STRING', label: 'String' },
    { value: 'INTEGER', label: 'Integer' },
    { value: 'FLOAT', label: 'Float' },
    { value: 'DATETIME', label: 'DateTime' },
    { value: 'TIMESTAMP', label: 'Timestamp' },
    { value: 'BOOLEAN', label: 'Boolean' },
  ];

  const parameterLocationOptions = [
    { value: 'BODY', label: 'Body' },
    { value: 'HEADER', label: 'Header' },
    { value: 'QUERY_STRING', label: 'Query string' },
  ];

  const addAttribute = () => {
    setSchemaAttributes([
      ...schemaAttributes,
      {
        key: UUID(),
        attribute_name: '',
        attribute_type: 'STRING',
        is_required: true,
        default_value: '',
        description: '',
        parameter_location: 'BODY',
      },
    ]);
  };

  const removeAttribute = (index) => {
    const newAttributes = schemaAttributes.filter((_, i) => i !== index);
    setSchemaAttributes(newAttributes);
  };

  const updateAttribute = (index, field, value) => {
    const newAttributes = [...schemaAttributes];
    newAttributes[index] = { ...newAttributes[index], [field]: value };
    setSchemaAttributes(newAttributes);
  };

  const isDisabled = !canEdit;

  return (
    <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
      <h4 className="h4-title">Schema Attributes</h4>
      <div className="small-font mb-2">Define the attributes schema for this connection object</div>

      {schemaAttributes.map((attr, index) => (
        <div key={attr.key || index} className="attribute-row mb-2 p-2 border-yes radius-yes">
          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Attribute Name</div>
              <div className="small-font">Enter attribute name</div>
            </div>
            <div className="align-wrapper d-flex align-center gap">
              <CustomInput
                id={`attr-name-${index}`}
                label="Attribute Name"
                placeholder="Enter attribute name"
                type="text"
                value={attr.attribute_name}
                onChange={(id, value) => updateAttribute(index, 'attribute_name', value)}
                disabled={isDisabled}
              />
              {!isDisabled && (
                <Button variant="danger" size="small" onClick={() => removeAttribute(index)}>
                  <i className="fa-solid fa-trash"></i>
                </Button>
              )}
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Type</div>
              <div className="small-font">Select attribute type</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomSelect
                id={`attr-type-${index}`}
                label="Type"
                options={attributeTypeOptions}
                value={attr.attribute_type}
                onChange={(value) => updateAttribute(index, 'attribute_type', value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Default Value</div>
              <div className="small-font">Enter default value (optional)</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomInput
                id={`attr-default-${index}`}
                label="Default Value"
                placeholder="Enter default value"
                type="text"
                value={attr.default_value || ''}
                onChange={(id, value) => updateAttribute(index, 'default_value', value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Required</div>
              <div className="small-font">Is this attribute required</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomToggle
                id={`attr-required-${index}`}
                label={attr.is_required ? 'Required' : 'Optional'}
                checked={attr.is_required}
                onChange={(id, value) => updateAttribute(index, 'is_required', value)}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="input-block d-flex flex-row justify-between">
            <div className="input-description">
              <div className="input-title">Description</div>
              <div className="small-font">Enter description (optional)</div>
            </div>
            <div className="align-wrapper d-flex align-center">
              <CustomTextarea
                id={`attr-desc-${index}`}
                label="Description"
                name={`description-${index}`}
                type="text"
                addedText={attr.description || ''}
                onChange={(value) => updateAttribute(index, 'description', value)}
                resizable={false}
                disabled={isDisabled}
                height="80px"
              />
            </div>
          </div>
        </div>
      ))}

      {!isDisabled && (
        <Button variant="secondary" size="medium" onClick={addAttribute}>
          <i className="fa-solid fa-plus"></i> Add Attribute
        </Button>
      )}
    </div>
  );
}

export default ConnectionObjectSchemaAttributes;
